import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useDASI, formatCurrency } from '../../context/DASIContext';
import './styles.scss';

const VectorItem = ({ vector }) => {
  const statusMap = {
    pass:     { icon: '✅', cls: 'td-passed',         label: '[Clean]' },
    recovery: { icon: '💰', cls: 'td-recovery-alert', label: '[RECOVERY ALERT]' },
    risk:     { icon: '⚠️', cls: 'td-risk-alert',     label: '[COMPLIANCE RISK]' },
    hitl:     { icon: '🤖', cls: 'td-hitl-alert',     label: '[HITL REQUIRED]' },
    cleared:  { icon: '✅', cls: 'td-passed',         label: '[Cleared]' },
  };
  const cfg = statusMap[vector.status] || statusMap.pass;

  return (
    <Box className={`td-vector-item ${cfg.cls}`}>
      <span className="td-v-icon">{cfg.icon}</span>
      <span className="td-v-label">{vector.name}</span>
      <span className="td-v-status">{cfg.label}</span>
    </Box>
  );
};

const DASIAuditRadar = () => {
  const navigate = useNavigate();
  const { entries, currentIndex, setCurrentIndex, clearException, clearHitl, validateHitl } = useDASI();
  const [activeTab, setActiveTab] = useState(0);
  const [validatingHitl, setValidatingHitl] = useState({});

  const entry = entries[currentIndex];

  const handleSelectEntry = (index) => {
    setCurrentIndex(index);
    setActiveTab(0);
  };

  const handleValidateHitl = (entryIndex, hitlIndex) => {
    const key = `${entryIndex}_${hitlIndex}`;
    setValidatingHitl((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => {
      validateHitl(entryIndex, hitlIndex);
      setValidatingHitl((prev) => ({ ...prev, [key]: false }));
    }, 1500);
  };

  const activeExCount = entry.exceptions.filter((ex) => !ex.cleared).length;
  const activeHitlCount = entry.hitl.filter((h) => !h.cleared).length;

  const badgeStyle = (count, activeColor) => ({
    background: count > 0 ? activeColor : '#cbd5e1',
    color: 'white',
    padding: '2px 6px',
    borderRadius: '10px',
    fontSize: '10px',
    marginLeft: '4px',
    display: 'inline-block',
  });

  return (
    <Box className="td-ledger-wrapper">
      {/* Left: Entry Queue */}
      <Box className="td-ledger-list">
        <Box className="td-ledger-queue-header">
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b' }}>
            Entry Audit Queue
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#64748b' }}>2 Exceptions out of 3</Typography>
        </Box>

        {entries.map((e, index) => {
          const indColors = e.indicators.map((ind) => {
            if (ind === 'fail') return '#ef4444';
            if (ind === 'pass') return '#10b981';
            return '#f59e0b';
          });

          return (
            <Box
              key={e.id}
              className={`td-ledger-item${currentIndex === index ? ' td-selected' : ''}`}
              onClick={() => handleSelectEntry(index)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>{e.id}</Typography>
              </Box>
              <Box sx={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                <span style={{ color: '#10b981' }}>Rec: {formatCurrency(e.recoveryValue)}</span>
                {' | '}
                <span style={{ color: '#ef4444' }}>Def: {formatCurrency(e.deficiencyValue)}</span>
              </Box>
              <Typography sx={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                Date: {e.date} | Bkr: {e.broker}
              </Typography>
              <Box sx={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                {indColors.map((color, i) => (
                  <span
                    key={i}
                    style={{ height: '8px', width: '8px', borderRadius: '50%', background: color, display: 'inline-block' }}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Right: Dossier */}
      <Box className="td-ledger-detail">
        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
          <Box>
            <Typography sx={{ margin: '0 0 4px 0', fontWeight: 700, fontSize: '22px', color: '#1e293b' }}>
              Entry Dossier: {entry.id}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
              Total Recoverable:{' '}
              <strong style={{ color: '#10b981' }}>{formatCurrency(entry.recoveryValue)}</strong>
              {' | '}
              Total Deficiency:{' '}
              <strong style={{ color: '#ef4444' }}>{formatCurrency(entry.deficiencyValue)}</strong>
            </Typography>
          </Box>
          <button className="td-primary-btn" onClick={() => navigate('/broker-resolution-desk', { state: { entryIndex: currentIndex } })}>
            Draft PSC Resolution Text
          </button>
        </Box>

        {/* Tabs */}
        <Box className="td-dossier-tabs">
          <button
            className={`td-dossier-tab${activeTab === 0 ? ' td-tab-active' : ''}`}
            onClick={() => setActiveTab(0)}
          >
            1. Macro Overview (Matrix)
          </button>
          <button
            className={`td-dossier-tab${activeTab === 1 ? ' td-tab-active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            2. Exception Details
            <span style={badgeStyle(activeExCount, '#ef4444')}>{activeExCount}</span>
          </button>
          <button
            className={`td-dossier-tab${activeTab === 2 ? ' td-tab-active' : ''}`}
            onClick={() => setActiveTab(2)}
          >
            3. HITL Actions
            <span style={badgeStyle(activeHitlCount, '#f59e0b')}>{activeHitlCount}</span>
          </button>
        </Box>

        {/* Tab 1: Macro Overview */}
        {activeTab === 0 && (
          <Box>
            <Box className="td-card">
              <Box className="td-card-header td-engine-pivot">
                <span>📊 Pillar 1: Pivot Row vs. 7501 Line Reconciliation</span>
              </Box>
              <Box className="td-card-body">
                <Box className="td-vector-grid">
                  {entry.classVectors.map((v, i) => <VectorItem key={i} vector={v} />)}
                </Box>
              </Box>
            </Box>

            <Box className="td-card">
              <Box className="td-card-header td-engine-typeA">
                <span>🧮 Pillar 2: Deterministic Validation</span>
              </Box>
              <Box className="td-card-body">
                <Box className="td-vector-grid">
                  {entry.typeAVectors.map((v, i) => <VectorItem key={i} vector={v} />)}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Tab 2: Exception Details */}
        {activeTab === 1 && (
          <Box>
            {entry.exceptions.length === 0 ? (
              <Box className="td-empty-state">
                <Typography variant="h6" sx={{ marginBottom: '8px' }}>No Exceptions Found</Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  All line items reconciled flawlessly against corporate ground truth and AI extraction.
                </Typography>
              </Box>
            ) : (
              entry.exceptions.map((ex, exIndex) => (
                <Box
                  key={exIndex}
                  className={`td-exception-container td-${ex.type}`}
                  sx={{ opacity: ex.cleared ? 0.6 : 1 }}
                >
                  <Box className={`td-exception-header td-${ex.type}`}>
                    {ex.type === 'recovery' ? '💰' : '⚠️'} {ex.title}
                  </Box>
                  <table>
                    <thead>
                      <tr>
                        <th>Part Number / Description</th>
                        <th>ACE 7501 Declared</th>
                        <th>Pivot / AI Extraction Target</th>
                        <th>Exception Flag</th>
                        <th>Resolution Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ex.items.map((item, itemIndex) => (
                        <tr key={itemIndex} style={{ opacity: ex.cleared ? 0.5 : 1 }}>
                          <td style={{ fontWeight: 600 }}>{item.part}</td>
                          <td>{item.declared}</td>
                          <td>{item.target}</td>
                          <td style={{ fontWeight: 700 }}>{item.flag}</td>
                          <td>
                            <button
                              className="td-btn-dismiss"
                              disabled={ex.cleared}
                              onClick={() =>
                                clearException(currentIndex, exIndex, item.vectorTarget, ex.value, ex.type)
                              }
                            >
                              {ex.cleared ? '✓ Cleared' : '✕ Clear Exception'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Box className="td-exception-summary">{ex.summary}</Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {/* Tab 3: HITL Actions */}
        {activeTab === 2 && (
          <Box>
            <Typography className="td-hitl-radar-header">
              Pillar 3: Predictive Threat Scans Executed
            </Typography>
            <Box className="td-vector-grid" sx={{ marginBottom: '32px' }}>
              {entry.typeBVectors.map((v, i) => <VectorItem key={i} vector={v} />)}
            </Box>

            {entry.hitl.length === 0 ? (
              <Box className="td-empty-state">
                <Typography variant="h6" sx={{ marginBottom: '8px' }}>No Action Required</Typography>
                <Typography sx={{ fontSize: '14px' }}>
                  No supply chain anomalies or pending HITL verifications detected.
                </Typography>
              </Box>
            ) : (
              entry.hitl.map((h, hIndex) => {
                const validating = validatingHitl[`${currentIndex}_${hIndex}`];
                return (
                  <Box key={hIndex} className="td-hitl-card" sx={{ opacity: h.cleared ? 0.5 : 1 }}>
                    <Box className="td-hitl-header">
                      <span>🤖 Agentic Predictor Action Card</span>
                      <span className="td-hitl-badge">{h.cleared ? 'Cleared' : 'Action Required'}</span>
                    </Box>
                    <Box className="td-hitl-body">
                      <Typography className="td-hitl-title">{h.title}</Typography>
                      <Typography className="td-hitl-desc">{h.desc}</Typography>
                      <Box className="td-hitl-action-area">
                        <strong style={{ color: '#b45309', fontSize: '13px' }}>{h.actionTitle}</strong>
                        <br />
                        <span style={{ color: '#92400e', fontSize: '12px' }}>{h.actionDesc}</span>
                        <Box sx={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                          <button
                            className="td-hitl-btn"
                            disabled={h.cleared || !!validating}
                            style={h.validated ? { backgroundColor: '#10b981' } : {}}
                            onClick={() =>
                              !h.validated && !validating && handleValidateHitl(currentIndex, hIndex)
                            }
                          >
                            {h.validated ? '✓ Validated' : validating ? 'Analyzing...' : h.btnText}
                          </button>
                          <button
                            className="td-hitl-clear-btn"
                            disabled={h.cleared}
                            onClick={() => clearHitl(currentIndex, hIndex, h.vectorsTarget)}
                          >
                            {h.cleared ? '✓ Cleared' : '✕ Clear Exception (Verify)'}
                          </button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DASIAuditRadar;
